/*
 * Home screen — level selection (FR-NAV-01).
 *
 * Renders the 8 level cards as the application's home view. The unlock state
 * is read from storage (FR-DATA-04). Levels with index <= unlockedLevel are
 * interactive and navigate to the per-level intro route on click. Locked
 * levels are rendered as disabled buttons (non-clickable, visually muted).
 *
 * Endless Practice: shown only after Level 8 is passed. The storage schema
 * uses `unlockedLevel` as a monotonic pointer — passing Level N sets it to
 * N+1, so "passed Level 8" is `unlockedLevel >= 9` (there is no Level 9 to
 * unlock; the bump just signals full completion).
 *
 * The level metadata (name, preview runes) is hard-coded for the MVP. The
 * real per-level rune set lands with FR-DATA-02 once the CSV exposes it.
 * Placeholder names are drawn from the design mockup; preview runes use the
 * Elder Futhark Unicode block (U+16A0–U+16FF). When the bespoke rune font
 * eventually loads (M0-4), these glyphs upgrade in place.
 */

import { navigate } from '../router';
import { loadState } from '../data/storage';

const LEVEL_COUNT = 8;
const ENDLESS_PRACTICE_THRESHOLD = LEVEL_COUNT + 1; // unlockedLevel value that means "passed L8"

interface LevelMeta {
  id: number;
  name: string;
  preview: string;
}

// Five preview runes per level. Cycles through the Elder Futhark block in
// order; the canonical per-level rune assignment lands with FR-DATA-02.
const LEVELS: ReadonlyArray<LevelMeta> = [
  { id: 1, name: 'First Strokes', preview: 'ᚠᚢᚦᚨᚱ' },
  { id: 2, name: 'First Words', preview: 'ᚲᚷᚹᚺᚾ' },
  { id: 3, name: 'Joining Up', preview: 'ᛁᛃᛇᛈᛉ' },
  { id: 4, name: 'Short Lines', preview: 'ᛊᛏᛒᛖᛗ' },
  { id: 5, name: 'Deeper Words', preview: 'ᛚᛜᛞᛟᚺ' },
  { id: 6, name: 'Bound Letters', preview: 'ᚠᚱᚨᚾᛏ' },
  { id: 7, name: 'Long Form', preview: 'ᛒᛟᚱᚷᛟ' },
  { id: 8, name: 'Final Trials', preview: 'ᚦᚢᚱᛁᛊ' },
];

export function renderHome(mount: HTMLElement): void {
  const { unlockedLevel } = loadState();
  const endlessUnlocked = unlockedLevel >= ENDLESS_PRACTICE_THRESHOLD;

  const container = document.createElement('section');
  container.setAttribute('aria-labelledby', 'level-select-heading');

  const eyebrow = document.createElement('span');
  eyebrow.className = 'tag tag--eyebrow';
  eyebrow.textContent = 'Your hall';
  container.appendChild(eyebrow);

  const heading = document.createElement('h1');
  heading.id = 'level-select-heading';
  heading.style.marginTop = 'var(--space-4)';
  heading.textContent = 'Level Select';
  container.appendChild(heading);

  const sub = document.createElement('p');
  sub.style.marginTop = 'var(--space-3)';
  sub.style.color = 'var(--color-text-warm)';
  sub.textContent = 'Pick up where you left off.';
  container.appendChild(sub);

  const grid = document.createElement('div');
  grid.setAttribute('role', 'list');
  grid.dataset.testid = 'level-grid';
  grid.style.display = 'grid';
  grid.style.gridTemplateColumns = 'repeat(auto-fill, minmax(160px, 1fr))';
  grid.style.gap = 'var(--space-4)';
  grid.style.marginTop = 'var(--space-6)';

  for (const level of LEVELS) {
    grid.appendChild(renderLevelCard(level, unlockedLevel));
  }
  container.appendChild(grid);

  if (endlessUnlocked) {
    container.appendChild(renderEndlessCard());
  }

  mount.appendChild(container);
}

function renderLevelCard(level: LevelMeta, unlockedLevel: number): HTMLElement {
  const locked = level.id > unlockedLevel;

  // Wrap the button in a listitem-role container instead of putting
  // role="listitem" on the <button> itself: a button already has an
  // implicit role="button", and overriding it confuses assistive
  // tech (is this a control or a list item?). The wrapper carries the
  // list semantics; the button stays a clean interactive control.
  const item = document.createElement('div');
  item.setAttribute('role', 'listitem');

  // <button> + click handler keeps keyboard activation (Enter/Space) free.
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = locked ? 'card card--locked' : 'card';
  btn.dataset.levelId = String(level.id);
  btn.dataset.state = locked ? 'locked' : 'unlocked';
  btn.style.textAlign = 'left';
  btn.style.cursor = locked ? 'not-allowed' : 'pointer';
  btn.style.font = 'inherit';
  btn.style.color = 'inherit';

  if (locked) {
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('aria-label', `Level ${level.id} — locked`);
  } else {
    btn.setAttribute('aria-label', `Level ${level.id} — ${level.name}`);
    btn.addEventListener('click', () => {
      navigate(`level-intro?n=${level.id}`);
    });
  }

  const tag = document.createElement('span');
  tag.className = 'tag';
  tag.textContent = `Level ${level.id}`;
  btn.appendChild(tag);

  const name = document.createElement('div');
  name.style.marginTop = 'var(--space-3)';
  name.style.fontWeight = 'var(--font-weight-semibold)';
  name.style.fontSize = 'var(--font-size-body-l)';
  name.textContent = level.name;
  btn.appendChild(name);

  const status = document.createElement('div');
  status.style.marginTop = 'var(--space-2)';
  status.style.fontSize = 'var(--font-size-body-s)';
  status.style.color = 'var(--color-text-dim)';
  status.textContent = locked ? 'Locked' : 'Available';
  btn.appendChild(status);

  const runes = document.createElement('div');
  runes.className = 'rune';
  runes.style.marginTop = 'var(--space-4)';
  runes.style.fontSize = 'var(--font-size-display-s)';
  runes.style.letterSpacing = '0.1em';
  runes.textContent = locked ? '·····' : level.preview;
  btn.appendChild(runes);

  item.appendChild(btn);
  return item;
}

function renderEndlessCard(): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'card card--elevated';
  btn.dataset.testid = 'endless-practice';
  btn.style.marginTop = 'var(--space-6)';
  btn.style.width = '100%';
  btn.style.textAlign = 'left';
  btn.style.cursor = 'pointer';
  btn.style.font = 'inherit';
  btn.style.color = 'inherit';
  btn.setAttribute('aria-label', 'Endless practice');
  btn.addEventListener('click', () => {
    navigate('practice');
  });

  const tag = document.createElement('span');
  tag.className = 'tag tag--solid';
  tag.textContent = 'Endless';
  btn.appendChild(tag);

  const name = document.createElement('div');
  name.style.marginTop = 'var(--space-3)';
  name.style.fontWeight = 'var(--font-weight-semibold)';
  name.style.fontSize = 'var(--font-size-body-l)';
  name.textContent = 'Endless Practice';
  btn.appendChild(name);

  const sub = document.createElement('div');
  sub.style.marginTop = 'var(--space-2)';
  sub.style.fontSize = 'var(--font-size-body-s)';
  sub.style.color = 'var(--color-text-warm)';
  sub.textContent = 'Keep the runes sharp.';
  btn.appendChild(sub);

  return btn;
}
